import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { marginBp } from '@app/shared/price-calc';
import { api, ApiError } from '../api';
import { useMe } from '../auth/useMe';
import { formatPln } from '../catalog/money';
import { paybackLabel } from './ItemsPage';
import './finance.css';

/**
 * One row of "Dopożyczenia" (`GET /api/finance/rentals`): a position of a supplier's catalog with its finished
 * rentals. The cost and the margin come only for a role that sees the rentals' costs.
 */
export type RentalEarning = { priceId: string; supplierId: string; supplierName: string; name: string; rentals: number; revenueGrosze: number; costGrosze?: number; marginBp?: number | null };
type SortKey = 'supplierName' | 'name' | 'rentals' | 'revenueGrosze' | 'costGrosze' | 'marginBp';

/** Sorted by `key` (names in Polish order), empty values always last. */
export function sortRentalRows(rows: readonly RentalEarning[], key: SortKey, desc: boolean): RentalEarning[] {
  return [...rows].sort((a, b) => {
    const x = a[key];
    const y = b[key];
    if (x == null || y == null) return x == null ? (y == null ? 0 : 1) : -1;
    const c = typeof x === 'string' ? x.localeCompare(y as string, 'pl') : (x as number) - (y as number);
    return desc ? -c : c;
  });
}

/** The suppliers of the rows, by name (the filter's options). */
export function supplierOptions(rows: readonly RentalEarning[]): Array<{ id: string; name: string }> {
  const byId = new Map(rows.map((r) => [r.supplierId, r.supplierName]));
  return [...byId].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, 'pl'));
}

/** The rows of one supplier ('' = all). */
export const bySupplier = (rows: readonly RentalEarning[], supplierId: string): RentalEarning[] => (supplierId ? rows.filter((r) => r.supplierId === supplierId) : [...rows]);

/** Sums of a supplier's rows (or of all): the cost and the margin only when the rows carry the cost (rule 7a). */
export type RentalSum = { rentals: number; revenueGrosze: number; costGrosze?: number; marginBp?: number | null };
export type RentalGroup = { supplierId: string; supplierName: string; rows: RentalEarning[]; sum: RentalSum };

function sumOf(rows: readonly RentalEarning[], withCost: boolean): RentalSum {
  const rentals = rows.reduce((n, r) => n + r.rentals, 0);
  const revenueGrosze = rows.reduce((n, r) => n + r.revenueGrosze, 0);
  if (!withCost) return { rentals, revenueGrosze };
  const costGrosze = rows.reduce((n, r) => n + (r.costGrosze ?? 0), 0);
  return { rentals, revenueGrosze, costGrosze, marginBp: marginBp(revenueGrosze - costGrosze, revenueGrosze) };
}

/**
 * Owner 2026-10-23 14:35: „grupuj najpierw według dostawców i sumuj, a na koniec sumuj całość” — suppliers in Polish
 * name order, the rows of each sorted by `key`, a subtotal per supplier and the grand total (count, revenue; cost and
 * margin only when the rows have the cost).
 */
export function rentalGroups(rows: readonly RentalEarning[], key: SortKey, desc: boolean): { groups: RentalGroup[]; total: RentalSum } {
  const withCost = rows.some((r) => 'costGrosze' in r);
  const groups = supplierOptions(rows).map((s) => {
    const mine = rows.filter((r) => r.supplierId === s.id);
    return { supplierId: s.id, supplierName: s.name, rows: sortRentalRows(mine, key, desc), sum: sumOf(mine, withCost) };
  });
  return { groups, total: sumOf(rows, withCost) };
}

export const RENTAL_COLUMNS: Array<{ key: SortKey; label: string; num: boolean; cost?: boolean }> = [
  { key: 'supplierName', label: 'Dostawca', num: false },
  { key: 'name', label: 'Pozycja', num: false },
  { key: 'rentals', label: 'Dopożyczenia', num: true },
  { key: 'revenueGrosze', label: 'Przychód netto', num: true },
  { key: 'costGrosze', label: 'Koszt', num: true, cost: true },
  { key: 'marginBp', label: 'Marża', num: true, cost: true },
];

/**
 * "Finanse → Więcej → Dopożyczenia" (owner 2026-10-22 00:12, next to "Urządzenia"): per position of a supplier's
 * catalog in the chosen months — how many times it was rented (returned rentals), what the client paid for it
 * (accepted quotes, after discounts), what the rentals cost and the margin. The supplier filter works on the loaded
 * rows (every supplier of the period is in them).
 */
export function RentalsReportPage() {
  const { me } = useMe();
  const year = new Date().getFullYear();
  const [from, setFrom] = useState(`${year}-01`);
  const [to, setTo] = useState(`${year}-12`);
  const [supplierId, setSupplierId] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'revenueGrosze', desc: true });
  const valid = /^\d{4}-\d{2}$/.test(from) && /^\d{4}-\d{2}$/.test(to) && from <= to;
  const report = useQuery({
    queryKey: ['finance', 'rentals', from, to],
    enabled: valid,
    queryFn: () => api<{ positions: RentalEarning[] }>(`/api/finance/rentals?${new URLSearchParams({ from, to }).toString()}`),
  });
  if (!me) return null;
  const all = report.data?.positions ?? [];
  const options = supplierOptions(all);
  const rows = bySupplier(all, supplierId);
  const withCost = all.some((r) => 'costGrosze' in r);
  // grouped by supplier (owner 2026-10-23): the supplier is the group's header, not a column
  const columns = RENTAL_COLUMNS.filter((c) => c.key !== 'supplierName' && (!c.cost || withCost));
  const { groups, total } = rentalGroups(rows, sort.key, sort.desc);
  const sumCells = (sum: RentalSum) => (
    <>
      <td className="num">{sum.rentals}</td>
      <td className="num nowrap">{formatPln(sum.revenueGrosze)}</td>
      {withCost && (
        <>
          <td className="num nowrap">{sum.costGrosze == null ? '—' : formatPln(sum.costGrosze)}</td>
          <td className="num nowrap">{sum.marginBp == null ? '—' : paybackLabel(sum.marginBp)}</td>
        </>
      )}
    </>
  );
  const pick = (key: SortKey) => setSort((s) => (s.key === key ? { key, desc: !s.desc } : { key, desc: key !== 'name' && key !== 'supplierName' }));
  return (
    <section className="page wide" aria-label="Dopożyczenia" data-feature-id="finance.rentals">
      <p className="lede">Ile razy dopożyczaliśmy sprzęt od dostawców (zwrócone dopożyczenia), ile zapłacił za niego klient (zaakceptowane kosztorysy, po rabatach){withCost ? ', ile kosztował i jaka była marża' : ''}.</p>
      <div className="fin-add" role="group" aria-label="Okres i dostawca">
        <label className="field">
          <span>Od miesiąca</span>
          <input type="month" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="field">
          <span>Do miesiąca</span>
          <input type="month" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label className="field">
          <span>Dostawca</span>
          <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
            <option value="">Wszyscy</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {!valid && <p className="fin-warn">„Od” nie może być później niż „Do”.</p>}
      {report.error && (
        <p className="alert" role="alert">
          {report.error instanceof ApiError ? report.error.message : 'Nie udało się wczytać zestawienia'}
        </p>
      )}
      {report.data && rows.length === 0 && <p className="muted">W tym okresie nie ma zwróconych dopożyczeń z pozycji katalogów dostawców.</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table fin-table" aria-label="Rentowność dopożyczeń">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key} scope="col" className={c.num ? 'num' : undefined} aria-sort={sort.key === c.key ? (sort.desc ? 'descending' : 'ascending') : 'none'}>
                    <button className="sort-head" type="button" onClick={() => pick(c.key)}>
                      {c.label}
                      {sort.key === c.key ? (sort.desc ? ' ↓' : ' ↑') : ''}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            {groups.map((g) => (
              <tbody key={g.supplierId} className="fin-rental-group" aria-label={`Dostawca: ${g.supplierName}`}>
                <tr className="fin-group-head">
                  <th scope="colgroup" colSpan={columns.length}>
                    {g.supplierName}
                  </th>
                </tr>
                {g.rows.map((r) => (
                  <tr key={r.priceId}>
                    <td>{r.name}</td>
                    <td className="num">{r.rentals}</td>
                    <td className="num nowrap">{formatPln(r.revenueGrosze)}</td>
                    {withCost && (
                      <>
                        <td className="num nowrap">{r.costGrosze == null ? '—' : formatPln(r.costGrosze)}</td>
                        <td className="num nowrap">{r.marginBp == null ? '—' : paybackLabel(r.marginBp)}</td>
                      </>
                    )}
                  </tr>
                ))}
                {groups.length > 1 && (
                  <tr className="fin-group-sum">
                    <th scope="row">Razem {g.supplierName}</th>
                    {sumCells(g.sum)}
                  </tr>
                )}
              </tbody>
            ))}
            <tfoot>
              <tr className="fin-total">
                <th scope="row">Razem dopożyczenia</th>
                {sumCells(total)}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}
